import { useMemo, useContext, useCallback } from 'react';
import { Listing } from 'models/Listing';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { useTranslation } from 'react-i18next';
import { DropdownOption } from 'models/Dropdown';
import confirm from 'modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import { DealStage } from 'models/DealRoom';

type UseGenerateDropdownOptionsProps = {
  listing: Partial<Listing>;
  canPause: boolean;
  hasListing: boolean;
  isArchivedOrSold: boolean;
  canDeleteDraft: boolean;
  canProlongCampaign: boolean;
  handleClearDraft: (listing: Listing) => void;
  handlePauseListing: (dealRoomStatus: DealStage) => void;
  onChangeTier: () => void;
  onProlongCampaign: () => void;
};

export function useGenerateDropdownOptions(
  config: UseGenerateDropdownOptionsProps,
) {
  const {
    listing,
    hasListing,
    isArchivedOrSold,
    canDeleteDraft,
    canPause,
    canProlongCampaign = false,
    handleClearDraft,
    handlePauseListing,
    onChangeTier,
    onProlongCampaign,
  } = config;
  const { _id } = listing || {};

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const onClearDraft = useCallback(async () => {
    if (
      await confirm(
        {
          title: t('deleteDraft'),
          content: t('confirmDeleteDraft'),
          type: 'delete',
        },
        true,
      )
    ) {
      try {
        const { data } = await api.portfolio.seller.clearDraft(_id);
        popSuccess(t('successfullyRemovedDraft'));
        handleClearDraft(data);
      } catch (e) {
        popServerError(e);
      }
    }
  }, [_id, popServerError, popSuccess, t, handleClearDraft]);

  const onPause = useCallback(async () => {
    if (
      await confirm(
        {
          title: t('pauseListing'),
          content: t('confirmPauseListing'),
          type: 'delete',
        },
        true,
      )
    ) {
      try {
        const { data } = await api.portfolio.seller.pauseListing(_id);
        handlePauseListing(data.dealStage);
      } catch (e) {
        popServerError(e);
      }
    }
  }, [_id, t, handlePauseListing, popServerError]);

  const menuOptions: DropdownOption[] = useMemo(() => {
    if (isArchivedOrSold) return [];

    return [
      ...(canDeleteDraft
        ? [
            {
              label: t('deleteDraft'),
              onClick: onClearDraft,
            },
          ]
        : []),
      ...(canPause
        ? [
            {
              label: t('pauseListing'),
              onClick: onPause,
            },
          ]
        : []),
      ...(isSuperAdmin && canProlongCampaign
        ? [
            {
              label: t('extendCampaign'),
              onClick: onProlongCampaign,
            },
          ]
        : []),
      ...(isSuperAdmin && hasListing
        ? [
            {
              label: t('changeListingTier'),
              onClick: onChangeTier,
            },
          ]
        : []),
    ];
  }, [
    canDeleteDraft,
    isSuperAdmin,
    hasListing,
    isArchivedOrSold,
    canPause,
    canProlongCampaign,
    t,
    onClearDraft,
    onPause,
    onChangeTier,
    onProlongCampaign,
  ]);

  return { menuOptions };
}
